/**
 * Money formatting (PRD §9.5).
 *
 * Two presentations, and picking the wrong one is a real error rather than a
 * style preference:
 *
 *   - **`table`** abbreviates — `€340k`. A column of exact euro figures is
 *     unscannable, and the reader is comparing magnitudes, not auditing.
 *   - **`exact`** does not — `€340,000`. On a detail page the figure is the
 *     record, and an abbreviation there loses information the reader came for.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.5, §4.3
 */
import { MINUS, NUMERIC_LOCALE } from './constants';
import { formatSignificant, isFormattable } from './number';

/** How a money value is presented. */
export type MoneyStyle = 'table' | 'exact';

/** Abbreviation thresholds, largest first. */
const SCALES = [
  { threshold: 1_000_000_000, suffix: 'bn' },
  { threshold: 1_000_000, suffix: 'm' },
  { threshold: 1_000, suffix: 'k' },
] as const;

/**
 * Formats an amount in euros.
 *
 * @param value  The amount, in whole euros (not cents — the API converts at
 *   its boundary, so anything reaching the UI is already euros).
 * @param style  `'table'` abbreviates; `'exact'` writes the figure in full.
 */
export function formatMoney(value: number, style: MoneyStyle = 'table'): string {
  if (!isFormattable(value)) return '';

  if (style === 'exact') {
    return toEuroSign(
      new Intl.NumberFormat(NUMERIC_LOCALE, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value),
    );
  }

  const magnitude = Math.abs(value);
  const sign = value < 0 ? MINUS : '';

  for (const { threshold, suffix } of SCALES) {
    if (magnitude >= threshold) {
      /* Significant figures, not decimal places: €1.23m and €12.3m both carry
         three, which is what PRD §9.5 asks for in a table. */
      return `${sign}€${formatSignificant(magnitude / threshold, 'table')}${suffix}`;
    }
  }

  return `${sign}€${formatSignificant(magnitude, 'table')}`;
}

/**
 * Normalises the currency output.
 *
 * `Intl` may emit a narrow no-break space between symbol and digits depending
 * on locale data, and it uses a hyphen-minus for negatives. Both are replaced
 * so the output matches PRD §9.5 exactly rather than varying with the ICU
 * version the browser happens to ship.
 */
function toEuroSign(formatted: string): string {
  /* Escaped rather than literal: a bare NBSP is invisible in a diff, and the
     two characters here (U+00A0 and U+202F) look identical to a space. */
  return formatted.replace(/[\u00a0\u202f]/gu, '').replace(/-/gu, MINUS);
}
